import { styled } from '../../utils/styled'
import { type CSSObject } from '@mui/material/styles'
import { type Theme } from '../theme'
import { Box, Collapse, Drawer, List, ListItem } from '@mui/material'
import Grid from '../Grid'

const drawerWidth = 'min(max(308px, 20vw), 500px)'

const selectedNavBackground = (theme: Theme): string => theme.tokens.background.tonal.superSoft.pressed

const expandedNavBackground = (theme: Theme): string => theme.tokens.background.tonal.superSoft.pressed

const hoverNavBackground = (theme: Theme): string => theme.tokens.background.tonal.superSoft.hover

const shellNavBackground = (theme: Theme): string => theme.tokens.background.tonal.superSoft.default

const lockedNavColor = (theme: Theme): string => theme.tokens.foreground.main.secondary

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 500
  }),
  overflowX: 'hidden',
  background: shellNavBackground(theme)
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 500
  }),
  overflowX: 'hidden',
  width: '72px',
  background: shellNavBackground(theme)
})

const getNavItemBackground = (theme: Theme, isSelected?: boolean, isExpanded?: boolean): string => {
  if (isSelected === true) {
    return selectedNavBackground(theme)
  }
  if (isExpanded === true) {
    return expandedNavBackground(theme)
  }
  return 'none'
}

const getLockedForegroundStyles = (theme: Theme, isLocked?: boolean): CSSObject => {
  if (isLocked !== true) {
    return {}
  }

  return {
    color: lockedNavColor(theme),
    svg: {
      color: lockedNavColor(theme)
    }
  }
}

export const StyledGridDrawerFooter = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'flex-end',
  flexDirection: 'column',
  margin: '0px 12px',
  boxShadow: `inset 0 1px 0 ${theme.tokens.border.main.tonal}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -24,
    left: 0,
    right: 0,
    height: 24,
    background: `linear-gradient(to top, ${shellNavBackground(theme)}, transparent)`,
    pointerEvents: 'none'
  },
  'li.MuiListItem-root': {
    padding: '0px !important'
  }
}))

export const StyledGridFooterActionItemsContainer = styled(Grid)(() => ({
  width: '100%',
  paddingTop: '8px'
}))

export const StyledDrawer = styled(Drawer)(
  ({ theme, open }: any): CSSObject => ({
    width: drawerWidth,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiPaper-root': {
      border: 'initial',
      '& > .MuiList-root': {
        li: {
          padding: 0
        }
      }
    },
    '& .MuiListItem-root': {
      padding: '0 12px',
      '& a': {
        display: 'block',
        width: '100%'
      },
      '& .MuiListItemButton-root': {
        display: 'flex',
        width: '100%',
        minHeight: 48,
        paddingLeft: '12px',
        paddingRight: '12px',
        '& .MuiListItemIcon-root': {
          minWidth: 0,
          color: theme.tokens.foreground.main.primary
        },
        '& .MuiListItemText-root': {
          marginLeft: '32px',
          ...(open === true && {})
        }
      }
    },
    ...(open === true && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme)
    }),
    ...(open === false && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme)
    }),
    '& a': {
      color: 'inherit',
      textDecoration: 'none'
    }
  })
)

export const StyledListItemNavItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isExpanded' && prop !== 'isLocked' && prop !== 'isSelected'
})<{ isSelected?: boolean; isLocked?: boolean; isExpanded?: boolean }>(
  ({ isSelected, isLocked, isExpanded, theme }) => {
    const backgroundColor = getNavItemBackground(theme, isSelected, isExpanded)

    return {
      backgroundColor,
      borderRadius: theme.borderRadius.large,
      borderBottomLeftRadius: isExpanded === true ? 0 : theme.borderRadius.large,
      borderBottomRightRadius: isExpanded === true ? 0 : theme.borderRadius.large,
      margin: 0,
      '& .MuiListItemButton-root': {
        backgroundColor,
        borderRadius: theme.borderRadius.large,
        paddingLeft: '12px',
        paddingRight: '12px',
        ':hover': {
          backgroundColor: hoverNavBackground(theme)
        },
        ...getLockedForegroundStyles(theme, isLocked)
      }
    }
  }
)

// Used in NavContentItemWithChildren to avoid rendering <li> inside the outer <li> container.
// Shares the same visual styles as StyledListItemNavItem but renders as a div.
export const StyledDivNavItemHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isExpanded' && prop !== 'isSelected' && prop !== 'isLocked'
})<{ isSelected?: boolean; isExpanded?: boolean; isLocked?: boolean }>(
  ({ isSelected, isExpanded, isLocked, theme }) => {
    const headerBackgroundColor = getNavItemBackground(theme, isSelected, isExpanded)

    return {
      backgroundColor: headerBackgroundColor,
      borderRadius: theme.borderRadius.large,
      borderBottomLeftRadius: isExpanded === true ? 0 : theme.borderRadius.large,
      borderBottomRightRadius: isExpanded === true ? 0 : theme.borderRadius.large,
      margin: 0,
      '& .MuiListItemButton-root': {
        backgroundColor: headerBackgroundColor,
        borderRadius: theme.borderRadius.large,
        paddingLeft: '12px',
        paddingRight: '12px',
        ':hover': {
          backgroundColor: hoverNavBackground(theme)
        },
        ...getLockedForegroundStyles(theme, isLocked)
      },
      '& .MuiListItemIcon-root': {
        color: 'inherit'
      }
    }
  }
)

export const StyledBoxChevronContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isNavBarOpen'
})<{ isNavBarOpen: boolean }>(
  ({ isNavBarOpen, theme }): CSSObject => ({
    display: 'flex',
    marginLeft: '16px',
    opacity: isNavBarOpen ? 1 : 0,
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: isNavBarOpen ? 220 : 0,
      delay: isNavBarOpen ? 280 : 0
    })
  })
) as React.ComponentType<any>

export const StyledListFooterContainer = styled(List)<{ open: boolean }>(({ open, theme }) => ({
  width: '100%',
  paddingBottom: '16px',
  paddingTop: '4px',
  '> li.MuiListItem-root': {
    padding: '0 12px',
    '> .MuiListItemButton-root': {
      paddingLeft: '4px',
      paddingRight: '12px',
      '.MuiListItemText-root': {
        marginLeft: '24px'
      }
    }
  },
  '> div.MuiCollapse-root': {
    '.MuiButtonBase-root': {
      '.MuiListItemText-root': {
        marginLeft: '8px'
      }
    }
  },
  '.MuiListItemText-root': {
    marginTop: '0px',
    marginBottom: '0px',
    '.MuiListItemText-primary': {
      textOverflow: 'ellipsis',
      overflow: 'hidden'
    },
    '.MuiListItemText-secondary': {
      color: theme.tokens.foreground.main.secondary,
      marginTop: '-2px',
      lineHeight: '20px',
      marginBottom: '2px',
      textOverflow: 'ellipsis',
      overflow: 'hidden'
    },
    '.MuiListItemButton-root': {
      padding: '9px 16px 9px 20px',
      '.MuiListItemIcon-root': {
        minWidth: 0
      }
    },
    '.MuiCollapse-root': {
      marginLeft: '38px',
      visibility: open ? 'initial' : 'hidden'
    }
  },
  '.MuiButtonBase-root': {
    paddingLeft: '8px'
  },
  '> .MuiListItem-root': {
    '.MuiButtonBase-root': {
      paddingTop: 0,
      paddingBottom: 0
    }
  }
}))

export const StyledListItemLogoContainer = styled(ListItem)(() => ({
  '&.MuiListItem-root': {
    padding: '16px 12px !important',
    minHeight: 'none',
    '&.MuiListItem-gutters > .MuiListItemButton-root': {
      padding: '0 8px'
    }
  },
  display: 'block',
  padding: 0,
  '.MuiListItemButton-root': {
    width: '100%',
    padding: '0 16px 0 0'
  }
}))

export const StyledGridNavContentSubItemContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'isLocked' && prop !== 'isSelected'
})<{
  isSelected: boolean
  isLocked?: boolean
}>(({ isSelected, isLocked, theme }) => ({
  display: 'flex',
  gap: '32px',
  marginLeft: '4px',
  padding: '4px 16px 4px 8px',
  width: '100%',
  color: (() => {
    if (isLocked === true) {
      return lockedNavColor(theme)
    }
    if (isSelected) {
      return theme.tokens.foreground.main.link
    }
    return theme.tokens.foreground.main.primary
  })(),
  'h5, p': {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  svg: {
    display: isSelected || isLocked === true ? 'block' : 'none'
  },
  '&:hover': {
    svg: {
      display: 'block'
    },
    p: {
      ...theme.typography.h5
    }
  }
}))

export const StyledListNavContentContainer = styled(List)<{ open: boolean }>(({ open }) => ({
  overflowX: 'hidden',
  overflowY: 'auto',
  height: '100%',
  padding: '0 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  '.MuiCollapse-wrapper': {
    '.MuiListItem-root': {
      '&:hover': {
        cursor: 'pointer'
      }
    }
  }
}))

export const StyledCollapseNavItemChildren = styled(Collapse, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})<{ open: boolean; isSelected: boolean }>(({ open, isSelected, theme }) => ({
  margin: '0',
  backgroundColor: open ? (isSelected ? selectedNavBackground(theme) : expandedNavBackground(theme)) : 'transparent',
  borderRadius: theme.borderRadius.xLarge,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  '& .MuiCollapse-wrapperInner a': {
    paddingRight: '24px'
  },
  '& .MuiListItemButton-root': {
    paddingLeft: '12px',
    paddingRight: '12px'
  }
}))
